<div class="header">
  <div class="logo">B</div>
  <div class="title">{{ 'TEAMS_PANEL.TITLE' | translate }}</div>
</div>

@if (isReconnecting) {
  <div class="reconnecting">
    <div class="status-dot connecting"></div>
    <span>{{ 'TEAMS_PANEL.RECONNECTING' | translate }}</span>
  </div>
}

@if (panelStatus === 'LOADING') {
  <div class="loading">{{ 'TEAMS_PANEL.LOADING' | translate }}</div>
}

@if (panelStatus === 'NO_CALL') {
  <div class="no-recording">
    {{ 'TEAMS_PANEL.NO_CALL' | translate }}
    <p class="no-recording-hint">{{ 'TEAMS_PANEL.NO_CALL_HINT' | translate }}</p>
  </div>
}

@if (panelStatus === 'NO_ACCOUNT') {
  <div class="no-recording">
    {{ 'TEAMS_PANEL.NO_ACCOUNT' | translate }}
    <p class="no-recording-hint">{{ 'TEAMS_PANEL.NO_ACCOUNT_HINT' | translate }}</p>
  </div>
}

@if (panelStatus === 'NOT_A_PARTICIPANT') {
  <div class="no-recording">
    {{ 'TEAMS_PANEL.NOT_A_PARTICIPANT' | translate }}
    <p class="no-recording-hint">{{ 'TEAMS_PANEL.NOT_A_PARTICIPANT_HINT' | translate }}</p>
  </div>
}

@if (panelStatus === 'SESSION_INVALID') {
  <div class="no-recording">
    {{ 'TEAMS_PANEL.SESSION_INVALID' | translate }}
    <p class="no-recording-hint">{{ 'TEAMS_PANEL.SESSION_INVALID_HINT' | translate }}</p>
  </div>
}

@if (panelStatus === 'NOT_ENABLED') {
  <div class="no-recording">
    {{ 'TEAMS_PANEL.NOT_ENABLED' | translate }}
    <p class="no-recording-hint">{{ 'TEAMS_PANEL.NOT_ENABLED_HINT' | translate }}</p>
  </div>
}

@if (panelStatus === 'SCHEDULED') {
  <div class="no-recording">
    {{ 'TEAMS_PANEL.SCHEDULED' | translate }}
  </div>
}

@if (panelStatus === 'ENDED') {
  <div class="no-recording">
    @if (canResend) {
      <p>{{ 'TEAMS_PANEL.RESEND_PROMPT' | translate }}</p>
    } @else if (!resendSuccess) {
      <p>{{ 'TEAMS_PANEL.RECORDING_ENDED' | translate }}</p>
    }
    @if (callInfo !== null && callInfo.callUrl !== null) {
      <a [href]="callInfo.callUrl" target="_blank" rel="noopener noreferrer" class="ended-link">{{ 'TEAMS_PANEL.VIEW_IN_APP' | translate }} &#x2192;</a>
    }
    @if (callInfo !== null && (canResend || resendSuccess)) {
      <div class="ended-actions">
        @if (resendSuccess) {
          <p class="resend-success">{{ 'TEAMS_PANEL.RESEND_SUCCESS' | translate }}</p>
        } @else if (showResendConfirm) {
          <div class="disconnect-confirm">
            <p class="confirm-text">{{ 'TEAMS_PANEL.RESEND_CONFIRM' | translate }}</p>
            <div class="confirm-buttons">
              <button class="btn btn-primary" (click)="confirmResend()">{{ 'TEAMS_PANEL.RESEND_YES' | translate }}</button>
              <button class="btn btn-secondary" (click)="cancelResend()">{{ 'TEAMS_PANEL.RESEND_CANCEL' | translate }}</button>
            </div>
          </div>
        } @else {
          <button class="btn btn-primary" (click)="requestResend()" [disabled]="isResending">
            {{ (isResending ? 'TEAMS_PANEL.RESENDING' : 'TEAMS_PANEL.RESEND_BOT') | translate }}
          </button>
        }
        @if (resendError !== null) {
          <div class="error-text">{{ resendError }}</div>
        }
      </div>
    }
  </div>
}

@if (panelStatus === 'CONNECTING') {
  <div class="status">
    <div class="status-dot connecting"></div>
    <div class="status-text">{{ 'TEAMS_PANEL.JOINING_CALL' | translate }}</div>
  </div>
}

@if (panelStatus === 'RECORDING' || panelStatus === 'PAUSED' || panelStatus === 'HANGING_UP') {
  <div class="status">
    <div
      class="status-dot"
      [class.recording]="panelStatus === 'RECORDING'"
      [class.paused]="panelStatus === 'PAUSED'"
      [class.hanging-up]="panelStatus === 'HANGING_UP'"
    ></div>
    <div class="status-text">
      @if (panelStatus === 'RECORDING') {
        {{ 'TEAMS_PANEL.RECORDING_IN_PROGRESS' | translate }}
      }
      @if (panelStatus === 'PAUSED') {
        {{ 'TEAMS_PANEL.RECORDING_PAUSED' | translate }}
      }
      @if (panelStatus === 'HANGING_UP') {
        {{ 'TEAMS_PANEL.DISCONNECTING' | translate }}
      }
    </div>
  </div>

  <div class="section">
    <div class="section-title">{{ 'TEAMS_PANEL.CALL_INFO' | translate }}</div>
    <div class="info-row">
      <span class="info-label">{{ 'TEAMS_PANEL.DURATION' | translate }}</span>
      <span class="info-value">{{ elapsedTime }}</span>
    </div>
    @if (remainingTime !== null) {
      <div class="info-row">
        <span class="info-label">{{ (isOvertime ? 'TEAMS_PANEL.OVERTIME' : 'TEAMS_PANEL.REMAINING') | translate }}</span>
        <span class="info-value info-value-inline" [class.overtime]="isOvertime">
          @if (canExtend) {
            <button
              type="button"
              class="extend-icon-btn"
              (click)="extend()"
              [disabled]="isExtending"
              [attr.aria-label]="'TEAMS_PANEL.EXTEND_CALL' | translate"
              [title]="'TEAMS_PANEL.EXTEND_CALL' | translate"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="10" cy="14" r="8" />
                <path d="M10 10v4h3" />
                <path d="M18 2v6M15 5h6" />
              </svg>
            </button>
          }
          {{ remainingTime }}
        </span>
      </div>
    }
    <div class="info-row">
      <span class="info-label">{{ 'TEAMS_PANEL.STATUS' | translate }}</span>
      <span class="info-value">
        @if (panelStatus === 'RECORDING') {
          {{ 'TEAMS_PANEL.STATUS_RECORDING' | translate }}
        }
        @if (panelStatus === 'PAUSED') {
          {{ 'TEAMS_PANEL.STATUS_PAUSED' | translate }}
        }
        @if (panelStatus === 'HANGING_UP') {
          {{ 'TEAMS_PANEL.STATUS_DISCONNECTED' | translate }}
        }
      </span>
    </div>
  </div>

  @if (panelStatus !== 'HANGING_UP') {
    <div class="section">
      <div class="section-title">{{ 'TEAMS_PANEL.CONTROLS' | translate }}</div>
      @if (panelStatus === 'RECORDING') {
        <button class="btn btn-primary" (click)="pause()" [disabled]="actionInProgress">
          {{ (isPausing ? 'TEAMS_PANEL.PAUSING' : 'TEAMS_PANEL.PAUSE_RECORDING') | translate }}
        </button>
      }
      @if (panelStatus === 'PAUSED') {
        <button class="btn btn-primary" (click)="resume()" [disabled]="actionInProgress">
          {{ (isResuming ? 'TEAMS_PANEL.RESUMING' : 'TEAMS_PANEL.RESUME_RECORDING') | translate }}
        </button>
      }
      @if (showDisconnectConfirm) {
        <div class="disconnect-confirm">
          <p class="confirm-text">{{ 'TEAMS_PANEL.DISCONNECT_CONFIRM' | translate }}</p>
          <div class="confirm-buttons">
            <button class="btn btn-danger" (click)="confirmDisconnect()">
              {{ 'TEAMS_PANEL.DISCONNECT_YES' | translate }}
            </button>
            <button class="btn btn-secondary" (click)="cancelDisconnect()">
              {{ 'TEAMS_PANEL.DISCONNECT_CANCEL' | translate }}
            </button>
          </div>
        </div>
      } @else {
        <button class="btn btn-danger" (click)="requestDisconnect()" [disabled]="actionInProgress">
          {{ (isDisconnecting ? 'TEAMS_PANEL.DISCONNECTING' : 'TEAMS_PANEL.DISCONNECT_BOT') | translate }}
        </button>
      }
    </div>
  }

  @if (errorMessage !== null) {
    <div class="error-text">{{ errorMessage }}</div>
  }

  @if (callInfo !== null && callInfo.callUrl !== null) {
    <div class="link-section">
      <a [href]="callInfo.callUrl" target="_blank" rel="noopener noreferrer">{{ 'TEAMS_PANEL.OPEN_IN_APP' | translate }}</a>
    </div>
  }
}
